import {
  Button,
  Center,
  chakra,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  DrawerProps,
  Heading,
  Image,
  Link,
  StackDivider,
  VStack,
} from "@chakra-ui/react";
import { PropsWithChildren } from "react";
import {
  FaGamepad,
  FaHeart,
  FaHome,
  FaPatreon,
  FaQuestion,
  FaShoppingCart,
  FaTv,
  FaYoutube,
} from "react-icons/fa";
import { useMatch } from "react-router-dom";
import { InternalLink } from "../link";

type SideNavProps = Pick<DrawerProps, "isOpen" | "onClose" | "finalFocusRef">;
export const SideNav = (props: SideNavProps) => {
  const isPartnerShipsActive = !!useMatch("/partnerships");
  const isHomeActive = !!useMatch("/");
  const isFAQActive = !!useMatch("/faq");
  const isGDDActive = !!useMatch("/game-dev-docs");
  return (
    <Drawer {...props} placement="right">
      <DrawerOverlay />
      <DrawerContent
        background="linear-gradient(45deg, #220049, #3E0060);"
        onClick={props.onClose}
      >
        <DrawerBody p={0}>
          <nav>
            <VStack divider={<StackDivider />} spacing={0}>
              <Center p={5} w="full">
                <Image
                  w="150px"
                  h="150px"
                  borderRadius={"full"}
                  src="/icon.png"
                  objectFit={"contain"}
                />
              </Center>
              <SideNavInternalItem
                icon={<FaHome />}
                active={isHomeActive}
                href="/"
              >
                Home
              </SideNavInternalItem>
              <SideNavItem
                active={false}
                icon={<FaShoppingCart />}
                href="https://www.youtube.com/@ThatGuyGlen/store"
                newItem={true}
              >
                Store
              </SideNavItem>
              <SideNavInternalItem
                icon={<FaGamepad />}
                href="/partnerships"
                active={isPartnerShipsActive}
              >
                Partnerships
              </SideNavInternalItem>
              <SideNavInternalItem
                active={isGDDActive}
                icon={<FaTv />}
                href="/game-dev-docs"
              >
                Game Dev Docs
              </SideNavInternalItem>
              <SideNavItem
                icon={<FaPatreon />}
                href="https://www.patreon.com/ThatGuyGlen"
              >
                Patreon
              </SideNavItem>
              <SideNavItem
                icon={<FaYoutube />}
                href="https://www.youtube.com/@ThatGuyGlen"
              >
                YouTube
              </SideNavItem>
              <SideNavInternalItem
                active={isFAQActive}
                icon={<FaQuestion />}
                href="/faq"
              >
                FAQ
              </SideNavInternalItem>
            </VStack>
          </nav>
        </DrawerBody>
        <DrawerFooter>
          <Heading
            mt={10}
            as="h3"
            size="xs"
            w="full"
            fontWeight={"normal"}
            fontStyle="italic"
            textAlign="center"
          >
            <chakra.span color="pink.400" display={"inline-flex"}>
              <FaHeart />
            </chakra.span>{" "}
            by{" "}
            <Link
              color="pink.400"
              href="https://www.linkedin.com/in/samgielis/"
            >
              Sam Gielis
            </Link>
          </Heading>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

type SideNavItemProps = PropsWithChildren<{
  icon: JSX.Element;
  href: string;
  active?: boolean;
  newItem?: boolean;
}>;
const SideNavItem = ({
  icon,
  children,
  href,
  active,
  newItem,
}: SideNavItemProps) => {
  return (
    <Button
      w="full"
      leftIcon={icon}
      variant="ghost"
      as={"a"}
      target="_blank"
      href={href}
      borderRadius={0}
      py={8}
      _hover={{ bgColor: "#2d374866" }}
      _active={{ bgColor: "#2d374866" }}
      isActive={active}
      _after={
        newItem
          ? {
              content: `" NEW"`,
              position: "absolute",
              right: "20px" /* Pulls it slightly to the right */,
              backgroundColor: "var(--chakra-colors-cyan-200)",
              color: "var(--chakra-colors-gray-800)",
              fontSize: "9px",
              fontWeight: "bold",
              padding: "2px 5px",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }
          : undefined
      }
    >
      {children}
    </Button>
  );
};
const SideNavInternalItem = ({
  icon,
  children,
  href,
  active,
}: SideNavItemProps) => {
  return (
    <Button
      w="full"
      leftIcon={icon}
      variant="ghost"
      as={InternalLink}
      to={href}
      borderRadius={0}
      py={8}
      _hover={{ bgColor: "#2d374866" }}
      _active={{ bgColor: "#2d374866" }}
      isActive={active}
    >
      {children}
    </Button>
  );
};
